import { Component, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import Swal from "sweetalert2";
import { catchError, of } from "rxjs";

import { Utils } from "../../../../../shared/utils/Utils";
import {
  RiesgosDesplazamientos,
  RiesgosDesplazamientosPayload,
  RiesgosDesplazamientosService,
} from "../../../../../shared/services/riesgosDesplazamientos.service";
import { PesvDesplazamientosService } from "../../../../../shared/services/pesvDesplazamientos.service";
import { PropietarioService } from "../../../../../shared/services/propietarios.service";
import { TiposRiesgoVialService } from "../../../../../shared/services/tipos-riesgo-vial.service";
import { NivelRiesgoService } from "../../../../../shared/services/nivel-riesgo.service";

@Component({
  selector: "app-riesgos-desplazamientos",
  templateUrl: "./riesgos-desplazamientos.component.html",
  styleUrls: ["./riesgos-desplazamientos.component.scss"],
})
export class RiesgosDesplazamientosComponent implements OnInit {
  // ============ TABS PRINCIPALES (formulario / listado) ============
  activeTab = 1;

  // Desplazamiento padre
  idDesplazamiento!: number;
  desplazamiento: any = null;

  get desplazamientoActivo(): boolean {
    return Number(this.desplazamiento?.estado) === 1;
  }

  riesgoForm!: FormGroup;
  guardando = false;
  modoEdicion = false;
  idRiesgoEditando: number | null = null;

  // Catálogos
  colaboradores: any[] = [];
  cargandoColaboradores = false;

  tiposRiesgo: any[] = [];

  nivelesRiesgo: any[] = [];

  // Listado
  riesgos: RiesgosDesplazamientos[] = [];
  riesgosFiltrados: RiesgosDesplazamientos[] = [];
  cargandoListado = false;
  filtro = "";
  filtroEstado: number | null = null;
  page = 1;
  pageSize = 10;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private riesgosService: RiesgosDesplazamientosService,
    private desplazamientosService: PesvDesplazamientosService,
    private propietarioService: PropietarioService,
    private tiposRiesgoVialService: TiposRiesgoVialService,
    private nivelRiesgoService: NivelRiesgoService,
  ) {}

  ngOnInit(): void {
    this.idDesplazamiento = Number(this.route.snapshot.paramMap.get("id"));

    if (!this.idDesplazamiento) {
      Swal.fire("Error", "No se recibió un desplazamiento válido.", "error").then(() =>
        this.volver(),
      );
      return;
    }

    this.inicializarFormulario();
    this.cargarDesplazamiento();
    this.cargarColaboradores();
    this.cargarRiesgos();
    this.cargarTiposRiesgoVial();
    this.cargarNivelesRiesgo();
  }

  private inicializarFormulario(): void {
    this.riesgoForm = this.fb.group({
      tipo_riesgo: [null, Validators.required],
      descripcion: ["", Validators.required],
      nivel_riesgo: [null],
      control: [""],
      responsable: [null],
    });
  }

  // ============ Desplazamiento padre ============
  private cargarDesplazamiento(): void {
    this.desplazamientosService
      .getByIdRiesgo(this.idDesplazamiento)
      .pipe(catchError(() => of(null)))
      .subscribe((respuesta: any) => {
        const data = respuesta?.data ?? respuesta;
        this.desplazamiento = Array.isArray(data) ? data[0] ?? null : data ?? null;
        this.activeTab = this.desplazamientoActivo ? 1 : 2;
      });
  }

  // ============ Catálogos ============
  private cargarColaboradores(): void {
    this.cargandoColaboradores = true;

    this.propietarioService
      .getPlanAccion()
      .pipe(catchError(() => of([])))
      .subscribe({
        next: (response: any) => {
          this.colaboradores = this.normalizarLista(response).map((c: any) => ({
            ...c,
            nombreCompleto: c.nombreCompleto ?? "",
            cargo: c.cargoData?.cargos ?? "",
          }));
          this.cargandoColaboradores = false;
        },
        error: () => {
          this.colaboradores = [];
          this.cargandoColaboradores = false;
        },
      });
  }

  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  private cargarTiposRiesgoVial(): void {
    this.tiposRiesgoVialService
      .getAll()
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        this.tiposRiesgo = Array.isArray(response) ? response : response?.data ?? [];
      });
  }

  private cargarNivelesRiesgo(): void {
    this.nivelRiesgoService
      .getAll()
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        this.nivelesRiesgo = Array.isArray(response) ? response : response?.data ?? [];
      });
  }

  // ============ Helpers de presentación ============
  nombreResponsable(cedula?: string | null): string {
    if (!cedula) return "-";
    const c = this.colaboradores.find((x) => String(x.cedula) === String(cedula));
    return c?.nombreCompleto || String(cedula);
  }



  // ============ Listado ============
  cargarRiesgos(): void {
    this.cargandoListado = true;
    this.riesgosService.getByDesplazamiento(this.idDesplazamiento).subscribe({
      next: (data: any) => {
        this.riesgos = this.normalizarLista(data);
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.riesgos = [];
        this.riesgosFiltrados = [];
        this.cargandoListado = false;
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.toLowerCase().trim();
    this.riesgosFiltrados = this.riesgos.filter((r) => {
      const coincideTexto =
        !termino ||
        [
          r.tipo_riesgo,
          r.descripcion,
          r.nivel_riesgo,
          r.control,
          this.nombreResponsable(r.responsable),
        ]
          .filter((v) => v && v !== "-")
          .some((v) => String(v).toLowerCase().includes(termino));

      const coincideEstado = this.filtroEstado === null || r.estado === this.filtroEstado;
      return coincideTexto && coincideEstado;
    });
    this.page = 1;
  }

  // ============ Guardar / editar ============
  private aTextoONull(valor: any): string | null {
    if (valor === null || valor === undefined) return null;
    const t = String(valor).trim();
    return t === "" ? null : t;
  }

  private construirPayload(): RiesgosDesplazamientosPayload {
    const raw = this.riesgoForm.getRawValue();
    return {
      desplazamiento_id: this.idDesplazamiento,
      tipo_riesgo: String(raw.tipo_riesgo).trim(),
      descripcion: String(raw.descripcion).trim(),
      nivel_riesgo: this.aTextoONull(raw.nivel_riesgo),
      control: this.aTextoONull(raw.control),
      responsable: this.aTextoONull(raw.responsable),
    };
  }

  guardar(): void {
    if (!this.modoEdicion && !this.desplazamientoActivo) {
      Swal.fire("Desplazamiento inactivo", "No se pueden crear riesgos para un desplazamiento inactivo.", "warning");
      return;
    }

    if (this.riesgoForm.invalid) {
      this.riesgoForm.markAllAsTouched();
      Swal.fire("Campos obligatorios", "Completa todos los campos requeridos.", "warning");
      return;
    }

    const payload = this.construirPayload();
    this.guardando = true;

    const obs =
      this.modoEdicion && this.idRiesgoEditando
        ? this.riesgosService.update(this.idRiesgoEditando, payload)
        : this.riesgosService.create({ ...payload, estado: 1 });

    obs.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Riesgo guardado correctamente.", "success");
        this.cancelarEdicion();
        this.cargarRiesgos();
        this.activeTab = 2;
      },
      error: (error) => {
        this.guardando = false;
        Swal.fire(
          "Error",
          error?.error?.message || error?.error?.error || "No se pudo guardar el riesgo.",
          "error",
        );
      },
    });
  }

  editar(riesgo: RiesgosDesplazamientos): void {
    if (Number(riesgo.estado) !== 1) {
      Swal.fire("No se puede editar", "Solo se pueden editar riesgos activos.", "warning");
      return;
    }

    if (!riesgo.id) return;

    this.riesgosService.getById(riesgo.id).subscribe({
      next: (respuesta: any) => {
        const detalle = respuesta?.data ?? respuesta;
        this.modoEdicion = true;
        this.idRiesgoEditando = detalle.id ?? riesgo.id;
        this.riesgoForm.patchValue({
          tipo_riesgo: detalle.tipo_riesgo,
          descripcion: detalle.descripcion,
          nivel_riesgo: detalle.nivel_riesgo ?? null,
          control: detalle.control ?? "",
          responsable: detalle.responsable ?? null,
        });
        this.activeTab = 1;
      },
      error: () => Swal.fire("Error", "No se pudo cargar el riesgo para editar.", "error"),
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idRiesgoEditando = null;
    this.riesgoForm.reset();
    if (!this.desplazamientoActivo) this.activeTab = 2;
  }

  // ============ Activar / Inactivar ============
  inactivar(riesgo: RiesgosDesplazamientos): void {
    Swal.fire({
      title: "¿Inactivar riesgo?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, inactivar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !riesgo.id) return;
      this.riesgosService.inactivar(riesgo.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Riesgo inactivado.", "success");
          this.cargarRiesgos();
        },
        error: () => Swal.fire("Error", "No se pudo inactivar el riesgo.", "error"),
      });
    });
  }

  activar(riesgo: RiesgosDesplazamientos): void {
    Swal.fire({
      title: "¿Activar riesgo?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, activar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !riesgo.id) return;
      this.riesgosService.activar(riesgo.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Riesgo activado.", "success");
          this.cargarRiesgos();
        },
        error: () => Swal.fire("Error", "No se pudo activar el riesgo.", "error"),
      });
    });
  }

  // ============ Navegación ============
  volver(): void {
    this.location.back();
  }

  // ============ Exportar ============
  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }
}